import {
    CognitoAuth,
    CognitoAuthSession,
} from 'amazon-cognito-auth-js/dist/amazon-cognito-auth';
import {CognitoUserPool, CognitoUserSession} from 'amazon-cognito-identity-js';
import {config as AWSConfig} from 'aws-sdk';

AWSConfig.region = process.env.REACT_APP_AWS_REGION;

const clientId =
    process.env.REACT_APP_ENV_MONKIER == 'Dev'
        ? process.env.REACT_APP_PayLOGIKDevClientAppId
        : process.env.REACT_APP_PayLOGIKProdClientAppId;

// Creates a CognitoAuth instance
const createCognitoAuth = (): CognitoAuth => {
    const appWebDomain = process.env.REACT_APP_CustomAuthDomain.replace(
        'https://',
        ''
    ).replace('http://', '');
    const auth = new CognitoAuth({
        UserPoolId: process.env.REACT_APP_LogikUserPool,
        ClientId: clientId,
        AppWebDomain: appWebDomain,
        TokenScopesArray: ['openid', 'email', 'profile'],
        RedirectUriSignIn: process.env.REACT_APP_REDIRECT_URI,
        RedirectUriSignOut: process.env.REACT_APP_SIGNOUT_URI,
    });
    return auth;
};

// Creates a CognitoUserPool instance
const createCognitoUserPool = () =>
    new CognitoUserPool({
        UserPoolId: process.env.REACT_APP_LogikUserPool,
        ClientId: clientId,
    });

const getCurrentUser = () => {
    const pool = createCognitoUserPool();
    return pool.getCurrentUser();
};

interface UserSession {
    credentials: {
        accessToken: string;
        idToken: string;
        refreshToken: string;
    };
    user: {[key: string]: any};
}

export const cognitoUtils = {
    createCognitoUser: getCurrentUser,

    // Get the URI of the hosted sign in screen
    getCognitoSignInUri: () => {
        const signinUri = `https://${process.env.REACT_APP_CustomAuthDomain}/${process.env.REACT_APP_AWS_AUTHPATH}?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${process.env.REACT_APP_REDIRECT_URI}`;
        return signinUri;
    },

    // Parse the response from a Cognito callback URI (assumed a token or code is in the supplied href). Returns a promise.
    parseCognitoWebResponse: (href: string) => {
        return new Promise(
            (resolve: (result: CognitoAuthSession) => void, reject) => {
                const auth = createCognitoAuth();

                // userHandler will trigger the promise
                auth.userhandler = {
                    onSuccess: function (result: CognitoAuthSession) {
                        resolve(result);
                    },
                    onFailure: function (err) {
                        reject(
                            new Error(
                                'Failure parsing Cognito web response: ' + err
                            )
                        );
                    },
                };
                auth.parseCognitoWebResponse(href);
            }
        );
    },

    // Gets a new Cognito session. Returns a promise.
    getCognitoSession: () => {
        return new Promise(
            (resolve: (session: UserSession) => void, reject) => {
                const cognitoUser = getCurrentUser();
                if (!cognitoUser) {
                    return undefined;
                }

                cognitoUser.getSession((err, result: CognitoUserSession) => {
                    if (err || !result) {
                        reject(
                            new Error('Failure getting Cognito session: ' + err)
                        );
                        return;
                    }
                    const session = {
                        credentials: {
                            accessToken: result.getAccessToken().getJwtToken(),
                            idToken: result.getIdToken().getJwtToken(),
                            refreshToken: result.getRefreshToken().getToken(),
                        },
                        user: result.getIdToken().payload,
                    } as UserSession;
                    resolve(session);
                });
            }
        );
    },

    // Sign out of the current session (will redirect to signout URI)
    signOutCognitoSession: () => {
        const auth = createCognitoAuth();
        auth.signOut();
    },
};

const getCognitoToken = async () => {
    const session = await cognitoUtils.getCognitoSession();

    return session.credentials.idToken;
};

const AwsCognitoProvider = {
    authUseEffect: (
        onLogin: (
            idToken: string,
            user: {[key: string]: any},
            provider: string
        ) => void,
        onError: (error: any) => void
    ) => {
        if (window.location.hash || window.location.search) {
            cognitoUtils
                .parseCognitoWebResponse(window.location.href) // parse the callback URL
                .then(() => cognitoUtils.getCognitoSession()) // get a new session
                .then((session) => window.location.replace('/'))
                .catch((error) => {
                    console.log(error);
                    onError(error);
                });

            return;
        }

        if (!cognitoUtils.createCognitoUser()) {
            window.location.replace(cognitoUtils.getCognitoSignInUri());
            return;
        } else {
            cognitoUtils
                .getCognitoSession() // get a new session
                .then((session) => {
                    onLogin(
                        session.credentials.idToken,
                        session.user,
                        'AWSCognito'
                    );
                })
                .catch((error) => {
                    console.log(error);
                    onError(error);
                });
        }
    },
    getToken: getCognitoToken,
    logout: () => {
        const auth = createCognitoAuth();
        auth.signOut();
    },
};

export default AwsCognitoProvider;
